/**
 * Workspace store slice for workspace management
 * Handles current workspace, workspace switching, and workspace data
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { devtoolsDev } from '../middleware'
import { api } from '@/utils/api-client'

/**
 * Workspace interface matching backend model
 */
export interface Workspace {
  id: string
  name: string
  description?: string
  stripeCustomerId?: string
  defaultApiKey?: string
  settings?: Record<string, any>
  modelToUse?: string
  createdAt: Date
  updatedAt?: Date
}

/**
 * Workspace store state interface
 */
interface WorkspaceState {
  currentWorkspace: Workspace | null
  workspaces: Workspace[]
  isLoading: boolean
  error: string | null
}

/**
 * Workspace store actions interface
 */
interface WorkspaceActions {
  setCurrentWorkspace: (workspace: Workspace | null) => void
  setWorkspaces: (workspaces: Workspace[]) => void
  switchWorkspace: (workspaceId: string) => Promise<void>
  fetchWorkspaces: () => Promise<void>
  createWorkspace: (data: { name: string; description?: string }) => Promise<Workspace>
  updateWorkspace: (workspaceId: string, data: Partial<Workspace>) => Promise<void>
  deleteWorkspace: (workspaceId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

/**
 * Complete workspace store interface
 */
export interface WorkspaceStore extends WorkspaceState, WorkspaceActions {}

/**
 * Initial workspace state
 */
const initialWorkspaceState: WorkspaceState = {
  currentWorkspace: null,
  workspaces: [],
  isLoading: false,
  error: null
}

/**
 * Creates the workspace store with actions
 */
const createWorkspaceStore = () => create<WorkspaceStore>()(
  devtoolsDev(
    // @ts-ignore
    immer((set, get) => ({
        // Initial state
        ...initialWorkspaceState,

        // Actions
        setCurrentWorkspace: (workspace: Workspace | null) => {
          set((state) => {
            state.currentWorkspace = workspace
            state.error = null
          })
        },

        setWorkspaces: (workspaces: Workspace[]) => {
          set((state) => {
            state.workspaces = workspaces
            state.error = null
            
            // Auto-select first workspace if no current workspace is set
            if (!state.currentWorkspace && workspaces.length > 0) {
              state.currentWorkspace = workspaces[0]  // Fix: use index 0, not 1
            }
          })
        },

        switchWorkspace: async (workspaceId: string) => {
          const currentState = get()
          
          // Find workspace in current list
          const targetWorkspace = currentState.workspaces.find(w => w.id === workspaceId)
          if (!targetWorkspace) {
            set((state) => {
              state.error = 'Workspace not found'
            })
            throw new Error('Workspace not found')
          }

          set((state) => {
            state.isLoading = true
            state.error = null
          })

          try {
            // TODO: Call API to switch workspace context if needed
            // For now, just update local state
            set((state) => {
              state.currentWorkspace = targetWorkspace
              state.isLoading = false
            })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to switch workspace'
            set((state) => {
              state.isLoading = false
              state.error = errorMessage
            })
            throw error
          }
        },

        fetchWorkspaces: async () => {
          set((state) => {
            state.isLoading = true
            state.error = null
          })

          try {
            // Workspaces should primarily come from login/session APIs
            // This method is for explicit refresh if needed
            const response = await api.get('/workspaces', {
              requiresAuth: true
            })

            if (response && response.workspaces) {
              set((state) => {
                state.workspaces = response.workspaces
                // Set first workspace as current if none selected
                if (!state.currentWorkspace && response.workspaces.length > 0) {
                  state.currentWorkspace = response.workspaces[0]  // Fix: use index 0, not 1
                }
                state.isLoading = false
              })
            } else {
              throw new Error('No workspace data received')
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch workspaces'
            set((state) => {
              state.isLoading = false
              state.error = errorMessage
            })
            throw error
          }
        },

        createWorkspace: async (data: { name: string; description?: string }) => {
          set((state) => {
            state.isLoading = true
            state.error = null
          })

          try {
            // TODO: Replace with actual API call
            // const response = await api.post<{ workspace: Workspace }>('/workspaces', data)
            
            // Mock implementation
            const newWorkspace: Workspace = {
              id: Date.now().toString(),
              name: data.name,
              description: data.description,
              createdAt: new Date(),
              updatedAt: new Date()
            }

            set((state) => {
              state.workspaces.push(newWorkspace)
              state.isLoading = false
            })

            return newWorkspace
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to create workspace'
            set((state) => {
              state.isLoading = false
              state.error = errorMessage
            })
            throw error
          }
        },

        updateWorkspace: async (workspaceId: string, data: Partial<Workspace>) => {
          set((state) => {
            state.isLoading = true
            state.error = null
          })

          try {
            // TODO: Replace with actual API call
            // const response = await api.patch<{ workspace: Workspace }>(`/workspaces/${workspaceId}`, data)
            
            // Mock implementation
            set((state) => {
              const index = state.workspaces.findIndex(w => w.id === workspaceId)
              if (index !== -1) {
                state.workspaces[index] = { ...state.workspaces[index], ...data, updatedAt: new Date() }
                
                // Update current workspace if it's the one being updated
                if (state.currentWorkspace?.id === workspaceId) {
                  state.currentWorkspace = state.workspaces[index]
                }
              }
              state.isLoading = false
            })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update workspace'
            set((state) => {
              state.isLoading = false
              state.error = errorMessage
            })
            throw error
          }
        },

        deleteWorkspace: async (workspaceId: string) => {
          set((state) => {
            state.isLoading = true
            state.error = null
          })

          try {
            // TODO: Replace with actual API call
            // await api.delete(`/workspaces/${workspaceId}`)
            
            // Mock implementation
            set((state) => {
              state.workspaces = state.workspaces.filter(w => w.id !== workspaceId)
              
              // If deleting current workspace, switch to first available
              if (state.currentWorkspace?.id === workspaceId) {
                state.currentWorkspace = state.workspaces.length > 0 ? state.workspaces[0] : null
              }
              state.isLoading = false
            })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete workspace'
            set((state) => {
              state.isLoading = false
              state.error = errorMessage
            })
            throw error
          }
        },

        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading
          })
        },

        setError: (error: string | null) => {
          set((state) => {
            state.error = error
          })
        },

        clearError: () => {
          set((state) => {
            state.error = null
          })
        }
      })),
    'WorkspaceStore'
  )
)

/**
 * Workspace store instance
 */
export const workspaceStore = createWorkspaceStore()

/**
 * Workspace store hook for components
 * @returns Workspace store state and actions
 */
export const useWorkspaceStore = () => workspaceStore()

/**
 * Hook for current workspace only
 * @returns Current workspace or null
 */
export const useCurrentWorkspace = () => {
  return workspaceStore((state) => state.currentWorkspace)
}

/**
 * Hook for workspaces list only
 * @returns Array of workspaces
 */
export const useWorkspaces = () => {
  return workspaceStore((state) => state.workspaces)
}

/**
 * Hook for workspace loading state
 * @returns Loading state and error
 */
export const useWorkspaceStatus = () => {
  const { isLoading, error, clearError } = useWorkspaceStore()
  
  return {
    isLoading,
    error,
    clearError
  }
}

/**
 * Hook for workspace actions only
 * @returns Workspace actions
 */
export const useWorkspaceActions = () => {
  const {
    setCurrentWorkspace,
    setWorkspaces,
    switchWorkspace,
    fetchWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    setLoading,
    setError,
    clearError
  } = useWorkspaceStore()

  return {
    setCurrentWorkspace,
    setWorkspaces,
    switchWorkspace,
    fetchWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    setLoading,
    setError,
    clearError
  }
}